import { Box, Card, Grid, Typography, Button } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import React, { useState, useCallback, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import useAxios from '../../../../../hooks/useAxios'
import {
  IShopPerformance,
  IShopPerformanceIntervals,
} from '../../../../../types/tiktok'
import { Table, TableColumnsType, Spin, Empty, message, Tooltip } from 'antd'
import { FiCopy } from 'react-icons/fi'
import { SearchOutlined, InfoOutlined } from '@mui/icons-material'

interface ShopPerformanceData extends IShopPerformanceIntervals {
  key: string
  numberRow: number
}

const OrderReportCard: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(7, 'day'),
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())
  const [visitedReport, setVisitedReport] = useState<boolean>(true)
  const [searchParams, setSearchParams] = useState({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  })

  // Fetch shop performance data
  const shopPerformance = useAxios<IShopPerformance>(
    visitedReport
      ? {
          method: 'GET',
          url: '/tiktok/report/shop',
          params: {
            startDate: searchParams.startDate,
            endDate: searchParams.endDate,
            granularity: '1D',
          },
        }
      : null,
  )

  // Extract intervals data and sort by date (newest first)
  const intervals = shopPerformance.data?.data?.performance?.intervals || []

  // Handle date changes
  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date)
  }

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date)
  }

  // Handle search button click
  const handleSearch = useCallback(() => {
    const formattedStartDate = startDate ? startDate.format('YYYY-MM-DD') : ''
    const formattedEndDate = endDate ? endDate.format('YYYY-MM-DD') : ''

    setSearchParams({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    })
    setVisitedReport(true)
  }, [startDate, endDate])

  // Format currency values
  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text)
    message.success(`คัดลอกแล้ว: ${text}`)
  }

  // Define columns for Antd Table
  const columns: TableColumnsType<ShopPerformanceData> = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center',
      width: 80,
      fixed: 'left',
      render: (numberRow: number) => (
        <Typography
          variant="subtitle1"
          color="text.secondary"
          textAlign="center"
        >
          {numberRow}
        </Typography>
      ),
    },
    {
      title: 'วันที่',
      dataIndex: 'start_date',
      key: 'start_date',
      align: 'center',
      width: 120,
      fixed: 'left',
      render: (date: string) => (
        <Typography variant="subtitle1">
          {dayjs(date).format('DD/MM/YYYY')}
        </Typography>
      ),
    },
    {
      title: 'ยอดขาย (GMV)',
      dataIndex: 'gmv',
      key: 'gmv',
      align: 'right',
      width: 150,
      render: (gmv: { amount: string; currency: string }) => (
        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={0.5}
        >
          <Typography variant="subtitle1" color="success.main">
            {formatCurrency(gmv.amount)} {gmv.currency}
          </Typography>
          <FiCopy
            size={14}
            style={{ cursor: 'pointer' }}
            onClick={() =>
              handleCopyText(`${formatCurrency(gmv.amount)} ${gmv.currency}`)
            }
          />
        </Box>
      ),
    },
    {
      title: (
        <Tooltip title="GMV Breakdowns แสดงรายละเอียดของยอดขายแยกตามประเภท">
          <span>
            GMV Breakdowns <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'gmv_breakdowns',
      key: 'gmv_breakdowns',
      align: 'right',
      width: 180,
      render: (
        breakdowns: { amount: string; currency: string; type: string }[],
      ) => (
        <Box>
          {breakdowns?.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {item.type}: {formatCurrency(item.amount)} {item.currency}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: 'SKU Orders',
      dataIndex: 'sku_orders',
      key: 'sku_orders',
      align: 'center',
      width: 120,
      render: (skuOrders: number) => (
        <Typography variant="subtitle1">{skuOrders}</Typography>
      ),
    },
    {
      title: 'จำนวนคำสั่งซื้อ',
      dataIndex: 'orders',
      key: 'orders',
      align: 'center',
      width: 120,
      render: (orders: number) => (
        <Typography variant="subtitle1">{orders}</Typography>
      ),
    },
    {
      title: 'มูลค่าเฉลี่ยต่อออเดอร์',
      dataIndex: 'avg_order_value',
      key: 'avg_order_value',
      align: 'right',
      width: 150,
      render: (avg: { amount: string; currency: string }) => (
        <Typography variant="subtitle1">
          {formatCurrency(avg.amount)} {avg.currency}
        </Typography>
      ),
    },
    {
      title: 'จำนวนสินค้าที่ขายได้',
      dataIndex: 'units_sold',
      key: 'units_sold',
      align: 'center',
      width: 150,
      render: (units: number) => (
        <Typography variant="subtitle1">{units}</Typography>
      ),
    },
    {
      title: 'จำนวนผู้ซื้อ',
      dataIndex: 'buyers',
      key: 'buyers',
      align: 'center',
      width: 120,
      render: (buyers: number) => (
        <Typography variant="subtitle1">{buyers}</Typography>
      ),
    },
    {
      title: (
        <Tooltip title="Buyer Breakdowns แสดงรายละเอียดของผู้ซื้อแยกตามประเภท">
          <span>
            Buyer Breakdowns <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'buyer_breakdowns',
      key: 'buyer_breakdowns',
      align: 'right',
      width: 180,
      render: (breakdowns: { amount: string; type: string }[]) => (
        <Box>
          {breakdowns?.map((item, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {item.type}: {item.amount}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      title: 'จำนวนเข้าชมสินค้า',
      dataIndex: 'product_impressions',
      key: 'product_impressions',
      align: 'center',
      width: 150,
      render: (impressions: number) => (
        <Typography variant="subtitle1">{impressions}</Typography>
      ),
    },
    {
      title: (
        <Tooltip title="รายละเอียดเพิ่มเติมเกี่ยวกับการเข้าชมสินค้า">
          <span>
            Page Views <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'product_impression_breakdowns',
      key: 'product_page_views',
      align: 'center',
      width: 120,
      render: (breakdowns: any[]) => (
        <Typography variant="subtitle1">
          {breakdowns?.[0]?.product_page_views || 0}
        </Typography>
      ),
    },
    {
      title: (
        <Tooltip title="จำนวนผู้เข้าชมสินค้าเฉลี่ย">
          <span>
            Avg Visitors <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'product_impression_breakdowns',
      key: 'avg_product_page_visitors',
      align: 'center',
      width: 120,
      render: (breakdowns: any[]) => (
        <Typography variant="subtitle1">
          {breakdowns?.[0]?.avg_product_page_visitors || 0}
        </Typography>
      ),
    },
    {
      title: (
        <Tooltip title="มูลค่าการคืนเงิน">
          <span>
            Refunds <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'product_impression_breakdowns',
      key: 'refunds',
      align: 'right',
      width: 150,
      render: (breakdowns: any[]) => {
        const refund = breakdowns?.[0]?.refunds
        return refund ? (
          <Typography variant="subtitle1">
            {formatCurrency(refund.amount)} {refund.currency}
          </Typography>
        ) : (
          <Typography variant="subtitle1">0</Typography>
        )
      },
    },
    {
      title: (
        <Tooltip title="จำนวนการยกเลิกและคืนสินค้า">
          <span>
            Cancellations <InfoOutlined style={{ fontSize: 14 }} />
          </span>
        </Tooltip>
      ),
      dataIndex: 'product_impression_breakdowns',
      key: 'cancellations_and_returns',
      align: 'center',
      width: 150,
      render: (breakdowns: any[]) => (
        <Typography variant="subtitle1">
          {breakdowns?.[0]?.cancellations_and_returns || 0}
        </Typography>
      ),
    },
  ]

  // Prepare data for Antd Table - sort by date (newest first)
  const tableData: ShopPerformanceData[] = [...intervals]
    .sort(
      (a, b) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
    )
    .map((interval, index) => ({
      ...interval,
      key: interval.start_date,
      numberRow: index + 1,
    }))

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        รายงานร้านค้า {shopPerformance.data?.shop?.name ?? ''}
      </Typography>

      {/* Date filters with search button */}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="วันที่เริ่มต้น"
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <DatePicker
              label="วันที่สิ้นสุด"
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSearch}
              startIcon={<SearchOutlined />}
              fullWidth
            >
              ค้นหา
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>

      {/* Antd Table */}
      <Spin spinning={shopPerformance.isLoading}>
        {shopPerformance.error ? (
          <Typography color="error" sx={{ my: 2 }}>
            เกิดข้อผิดพลาดในการโหลดข้อมูล: {shopPerformance.error.message}
          </Typography>
        ) : tableData.length > 0 ? (
          <Table
            columns={columns}
            dataSource={tableData}
            bordered
            size="middle"
            pagination={false}
            scroll={{ x: 1800, y: 1000 }}
          />
        ) : (
          <Empty description="ไม่พบข้อมูลในช่วงเวลาที่เลือก" />
        )}
      </Spin>
    </Card>
  )
}

export default OrderReportCard
